import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Lenis from 'lenis';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export default function SmoothScroll({ children }) {
  const location = useLocation();

  useEffect(() => {
    let lenis = null;
    let rafId = null;
    let ctx = null;

    const initScroll = () => {
      const isTouchDevice =
        typeof window !== 'undefined' &&
        ('ontouchstart' in window || navigator.maxTouchPoints > 0);

      // On touch devices, native 120Hz smooth scrolling is already optimal.
      // Only initialize Lenis momentum scrolling on desktop mice/trackpads.
      if (!isTouchDevice) {
        lenis = new Lenis({
          duration: 1.0,
          easing: t => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
          orientation: 'vertical',
          gestureOrientation: 'vertical',
          smoothWheel: true,
          syncTouch: false,
        });

        lenis.on('scroll', ScrollTrigger.update);

        function raf(time) {
          lenis.raf(time);
          rafId = requestAnimationFrame(raf);
        }
        rafId = requestAnimationFrame(raf);
      }

      // Initialize GSAP Smooth Entrance and Parallax Scroll Reveals
      ctx = gsap.context(() => {
        gsap.utils.toArray('.section-title, .section-eyebrow').forEach(elem => {
          gsap.fromTo(
            elem,
            { opacity: 0, y: 25 },
            {
              opacity: 1,
              y: 0,
              duration: 0.8,
              ease: 'power3.out',
              scrollTrigger: {
                trigger: elem,
                start: 'top 92%',
                toggleActions: 'play none none none',
              },
            }
          );
        });

        gsap.utils.toArray('.reveal').forEach((elem, i) => {
          gsap.fromTo(
            elem,
            { opacity: 0, y: 30 },
            {
              opacity: 1,
              y: 0,
              duration: 0.85,
              delay: (i % 3) * 0.08,
              ease: 'power3.out',
              scrollTrigger: {
                trigger: elem,
                start: 'top 92%',
                toggleActions: 'play none none none',
              },
            }
          );
        });
      });
    };

    // Defer initialization to idle time to eliminate Total Blocking Time (TBT)
    if ('requestIdleCallback' in window) {
      const idleId = requestIdleCallback(initScroll, { timeout: 1200 });
      return () => {
        if ('cancelIdleCallback' in window) cancelIdleCallback(idleId);
        if (ctx) ctx.revert();
        if (rafId) cancelAnimationFrame(rafId);
        if (lenis) lenis.destroy();
      };
    } else {
      const timer = setTimeout(initScroll, 50);
      return () => {
        clearTimeout(timer);
        if (ctx) ctx.revert();
        if (rafId) cancelAnimationFrame(rafId);
        if (lenis) lenis.destroy();
      };
    }
  }, []);

  // Reset scroll smoothly on page route changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return <>{children}</>;
}
